import DOSSIER_FACT_TYPES from './dossierFactTypes';
import { MOCK_OPERATOR } from './marketingOpsPaths';
import {
  appendAiChatAssistantTurn,
  appendAiChatUserMessage,
  messagesForAiChat,
} from './watchAiChat';

import type {
  WatchAiChat,
  WatchAssociation,
  WatchBuilding,
  WatchCompany,
  WatchDossierSearchKind,
  WatchFinancial,
  WatchIssueChatMessage,
  WatchKnownSupplierLink,
  WatchProduct,
  WatchTowerMockState,
} from './types';

export const DOSSIER_SEARCH_KINDS: WatchDossierSearchKind[] = [
  'products',
  'buildings',
  'associations',
  'financials',
  'known_list',
];

export const DOSSIER_SEARCH_PROMPT: Record<WatchDossierSearchKind, string> = {
  products: 'Search products',
  buildings: 'Search buildings',
  associations: 'Search associations',
  financials: 'Search financials',
  known_list: 'Search known list',
};

/** Kinds `lookupDossierPublicFacts` accepts. Known list is not one of them. */
const DOSSIER_PUBLIC_SEARCH_KIND: Partial<Record<WatchDossierSearchKind, string>> = {
  products: 'products',
  buildings: 'buildings',
  associations: 'associations',
  financials: 'financials',
};

/**
 * User turn for a dossier tab search button.
 * Asks the live chat to call lookupDossierPublicFacts for a tab.
 * Known list asks for buyers and suppliers from dossier facts.
 */
export function dossierTabSearchUserTurn(kind: WatchDossierSearchKind): string {
  const nestKind = DOSSIER_PUBLIC_SEARCH_KIND[kind];
  if (!nestKind) {
    return [
      `${DOSSIER_SEARCH_PROMPT[kind]}.`,
      'Report known buyers and known suppliers for this company from the dossier facts.',
      'Do not put JSON in the chat text.',
      DOSSIER_FACT_TYPES,
    ].join(' ');
  }
  return [
    `${DOSSIER_SEARCH_PROMPT[kind]}.`,
    `Call lookupDossierPublicFacts with kind ${nestKind}.`,
    'Do not put JSON in the chat text.',
    DOSSIER_FACT_TYPES,
  ].join(' ');
}

export interface DossierSearchHit {
  label: string,
  payload: Record<string, unknown>,
}

export interface DossierSearchExtract {
  searchKind: WatchDossierSearchKind,
  query: string,
  positive: boolean,
  imported: boolean,
  hits: DossierSearchHit[],
}

export type DossierSearchSendResult =
  | { ok: true, state: WatchTowerMockState }
  | { ok: false, error: 'missing_chat' | 'missing_company' | 'no_search_kind', state: WatchTowerMockState };

export type DossierSearchImportResult =
  | { ok: true, state: WatchTowerMockState, importedCount: number }
  | {
    ok: false,
    error: 'missing_chat' | 'missing_company' | 'no_hits' | 'already_imported',
    state: WatchTowerMockState,
  };

function nextChildId(state: WatchTowerMockState, pick: (company: WatchCompany) => { id: number }[]): number {
  return state.watchCompanies.reduce(
    (max, company) => pick(company).reduce((inner, item) => Math.max(inner, item.id), max),
    0,
  ) + 1;
}

function refineLabel(base: string, query: string, kind: WatchDossierSearchKind): string {
  const prefix = DOSSIER_SEARCH_PROMPT[kind].toLowerCase();
  const extra = query.trim().replace(new RegExp(`^${prefix}\\s*`, 'i'), '').trim();
  if (extra.length < 3 || extra.length > 48) {
    return base;
  }
  return `${base} (${extra})`;
}

export function isDossierSearchMissQuery(query: string): boolean {
  const text = query.trim().toLowerCase();
  if (!text) {
    return false;
  }
  return text === 'no'
    || text === 'none'
    || /\b(no hits|no results|nothing found|none found|empty search)\b/.test(text);
}

export function asDossierSearchExtract(
  value: Record<string, unknown> | null,
): DossierSearchExtract | null {
  if (!value || typeof value.searchKind !== 'string' || !Array.isArray(value.hits)) {
    return null;
  }
  if (!DOSSIER_SEARCH_KINDS.includes(value.searchKind as WatchDossierSearchKind)) {
    return null;
  }
  const hits = value.hits.filter((item): item is DossierSearchHit => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return false;
    }
    const row = item as { label?: unknown, payload?: unknown };
    return typeof row.label === 'string' && Boolean(row.payload) && typeof row.payload === 'object';
  });
  return {
    searchKind: value.searchKind as WatchDossierSearchKind,
    query: typeof value.query === 'string' ? value.query : '',
    positive: value.positive === true && hits.length > 0,
    imported: value.imported === true,
    hits,
  };
}

export function lastDossierSearchExtract(
  state: WatchTowerMockState,
  chatId: number,
): { message: WatchIssueChatMessage, extract: DossierSearchExtract } | null {
  const messages = messagesForAiChat(state, chatId).slice().reverse();
  const message = messages.find((item) => item.role === 'assistant' && item.extractedJson);
  if (!message) {
    return null;
  }
  const extract = asDossierSearchExtract(message.extractedJson);
  if (!extract) {
    return null;
  }
  return { message, extract };
}

function cannedProduct(company: WatchCompany, query: string): DossierSearchHit {
  const name = refineLabel('HelioRecycle rPET flake', query, 'products');
  return {
    label: name,
    payload: {
      name,
      code: 'HF-RPET-80',
      uid: `WT-AI-P-${company.id}-RPET`,
      productCategory: 'Recycled polymer',
      productType: 'input',
      hsCode: '3907.61',
      description: 'Mock search hit. Recycled PET flake for food-contact film.',
    },
  };
}

function cannedBuilding(company: WatchCompany, query: string): DossierSearchHit {
  const name = refineLabel('Rotterdam tank farm', query, 'buildings');
  return {
    label: name,
    payload: {
      name,
      uid: `WT-AI-B-${company.id}-RTM`,
      legalName: company.name,
      businessUnitType: 'Physical',
      assetCategory: ['Warehouse'],
      ownedLeased: 'Leased',
      addressCountry: 'NL',
      addressCity: 'Rotterdam',
    },
  };
}

function cannedAssociation(company: WatchCompany, query: string): DossierSearchHit {
  const name = refineLabel('L. Kovacs joined as plant director', query, 'associations');
  return {
    label: name,
    payload: {
      eventKind: 'person_joined',
      occurredOn: '2026-03-01',
      effectiveFrom: '2026-03-01',
      personUid: `P-AI-${company.id}-KOVACS`,
      personFullName: 'L. Kovacs',
      personNationality: company.addressCountry || 'DE',
      personRole: 'director',
      notes: name,
    },
  };
}

function cannedFinancial(query: string): DossierSearchHit {
  const name = refineLabel('FY2025 annual report', query, 'financials');
  return {
    label: name,
    payload: {
      periodStart: '2025-01-01',
      periodEnd: '2025-12-31',
      reportKind: 'annual',
      currency: 'EUR',
      revenue: 428000000,
      ebitda: 54000000,
      liquidity: 28000000,
      notes: name,
    },
  };
}

function cannedKnownList(
  state: WatchTowerMockState,
  company: WatchCompany,
): DossierSearchHit | null {
  const linked = new Set(
    state.knownSupplierLinks.flatMap((link) => {
      if (link.supplierWatchCompanyId === company.id) {
        return [link.buyerWatchCompanyId];
      }
      if (link.buyerWatchCompanyId === company.id) {
        return [link.supplierWatchCompanyId];
      }
      return [];
    }),
  );
  const other = state.watchCompanies.find((item) => (
    item.id !== company.id
    && item.status !== 'archived'
    && !linked.has(item.id)
  ));
  if (!other) {
    return null;
  }
  return {
    label: `${other.name} as known buyer`,
    payload: {
      buyerWatchCompanyId: other.id,
      supplierWatchCompanyId: company.id,
      watchProductId: company.products[0]?.id || null,
      addedBy: MOCK_OPERATOR,
    },
  };
}

function hitsForKind(
  state: WatchTowerMockState,
  company: WatchCompany,
  kind: WatchDossierSearchKind,
  query: string,
): DossierSearchHit[] {
  if (kind === 'products') {
    return [cannedProduct(company, query)];
  }
  if (kind === 'buildings') {
    return [cannedBuilding(company, query)];
  }
  if (kind === 'associations') {
    return [cannedAssociation(company, query)];
  }
  if (kind === 'financials') {
    return [cannedFinancial(query)];
  }
  const known = cannedKnownList(state, company);
  return known ? [known] : [];
}

function assistantText(
  company: WatchCompany,
  kind: WatchDossierSearchKind,
  query: string,
  hits: DossierSearchHit[],
): string {
  if (!hits.length) {
    return [
      `${company.name}.`,
      `Search ${kind.replace('_', ' ')} for "${query}".`,
      'No new rows. Import stays off.',
    ].join(' ');
  }
  const labels = hits.map((item) => item.label).join('; ');
  return [
    `${company.name}.`,
    `Search ${kind.replace('_', ' ')} for "${query}".`,
    `Positive hit: ${labels}.`,
    'Import writes those rows into the dossier.',
  ].join(' ');
}

function withChat(
  state: WatchTowerMockState,
  chatId: number,
  patch: Partial<WatchAiChat>,
): WatchTowerMockState {
  return {
    ...state,
    createChats: state.createChats.map((item) => (
      item.id === chatId ? { ...item, ...patch } : item
    )),
  };
}

export function setDossierSearchKind(
  state: WatchTowerMockState,
  chatId: number,
  kind: WatchDossierSearchKind,
): WatchTowerMockState {
  if (!state.createChats.some((item) => item.id === chatId)) {
    return state;
  }
  return withChat(state, chatId, { dossierSearchKind: kind });
}

export function sendDossierSearchToAi(
  state: WatchTowerMockState,
  input: {
    chatId: number,
    companyId: number,
    query: string,
    now?: Date,
  },
): DossierSearchSendResult {
  const now = input.now || new Date();
  const chat = state.createChats.find((item) => item.id === input.chatId);
  if (!chat) {
    return { ok: false, error: 'missing_chat', state };
  }
  const company = state.watchCompanies.find((item) => item.id === input.companyId);
  if (!company) {
    return { ok: false, error: 'missing_company', state };
  }
  const kind = chat.dossierSearchKind;
  if (!kind) {
    return { ok: false, error: 'no_search_kind', state };
  }
  const query = input.query.trim() || DOSSIER_SEARCH_PROMPT[kind];
  const hits = isDossierSearchMissQuery(query) ? [] : hitsForKind(state, company, kind, query);
  const extract: DossierSearchExtract = {
    searchKind: kind,
    query,
    positive: hits.length > 0,
    imported: false,
    hits,
  };
  const withSeeds = withChat(state, input.chatId, {
    dossierSearchKind: kind,
    pendingSeeds: hits.map((item) => item.payload),
  });
  return {
    ok: true,
    state: appendAiChatAssistantTurn(
      withSeeds,
      input.chatId,
      assistantText(company, kind, query, hits),
      extract as unknown as Record<string, unknown>,
      now,
      { taskKind: 'company_dossier', searchKind: kind, query },
    ),
  };
}

export function startDossierTabSearch(
  state: WatchTowerMockState,
  input: {
    chatId: number,
    companyId: number,
    kind: WatchDossierSearchKind,
    now?: Date,
  },
): DossierSearchSendResult {
  const prompt = DOSSIER_SEARCH_PROMPT[input.kind];
  const withKind = setDossierSearchKind(state, input.chatId, input.kind);
  const withUser = appendAiChatUserMessage(withKind, input.chatId, prompt, input.now);
  return sendDossierSearchToAi(withUser, {
    chatId: input.chatId,
    companyId: input.companyId,
    query: prompt,
    now: input.now,
  });
}

function productFromHit(raw: Record<string, unknown>, id: number): WatchProduct {
  const name = typeof raw.name === 'string' ? raw.name : `Search product ${id}`;
  return {
    id,
    name,
    code: typeof raw.code === 'string' ? raw.code : null,
    uid: typeof raw.uid === 'string' ? raw.uid : `WT-AI-P-${id}`,
    productCategory: typeof raw.productCategory === 'string' ? raw.productCategory : null,
    productType: typeof raw.productType === 'string' ? raw.productType : null,
    upstreamTier: null,
    dateOpened: null,
    dateClosed: null,
    volumeQty: null,
    volumeQtyUnit: null,
    relianceOnFreeNaturalResources: [],
    relianceOnFreeSocialResources: [],
    criticalityRating: null,
    circularityFromDownstreamWaste: null,
    hsCode: typeof raw.hsCode === 'string' ? raw.hsCode : null,
    description: typeof raw.description === 'string' ? raw.description : null,
  };
}

function buildingFromHit(raw: Record<string, unknown>, id: number): WatchBuilding {
  const name = typeof raw.name === 'string' ? raw.name : `Search building ${id}`;
  const category = Array.isArray(raw.assetCategory)
    ? raw.assetCategory.filter((item): item is string => typeof item === 'string')
    : [];
  return {
    id,
    name,
    uid: typeof raw.uid === 'string' ? raw.uid : `WT-AI-B-${id}`,
    legalName: typeof raw.legalName === 'string' ? raw.legalName : null,
    ownership: null,
    equityShareholding: null,
    financialControl: null,
    businessUnitType: typeof raw.businessUnitType === 'string' ? raw.businessUnitType : null,
    assetCategory: category,
    ownedLeased: typeof raw.ownedLeased === 'string' ? raw.ownedLeased : null,
    numberOfEmployees: null,
    dateOpened: null,
    dateClosed: null,
    startDate: null,
    endDate: null,
    naceSection: null,
    naceCode: null,
    euTaxonomyCode: null,
    netBookAmount: null,
    netBookCurrency: null,
    addressCountry: typeof raw.addressCountry === 'string' ? raw.addressCountry : null,
    addressState: null,
    addressPostCode: null,
    addressCity: typeof raw.addressCity === 'string' ? raw.addressCity : null,
    addressStreet: null,
    addressBuilding: null,
    addressCoordinates: null,
  };
}

function associationFromHit(raw: Record<string, unknown>, id: number): WatchAssociation {
  return {
    id,
    eventKind: typeof raw.eventKind === 'string' ? raw.eventKind : 'person_joined',
    occurredOn: typeof raw.occurredOn === 'string' ? raw.occurredOn : '2026-01-01',
    effectiveFrom: typeof raw.effectiveFrom === 'string' ? raw.effectiveFrom : null,
    effectiveTo: null,
    relatedWatchCompanyId: typeof raw.relatedWatchCompanyId === 'number' ? raw.relatedWatchCompanyId : null,
    personUid: typeof raw.personUid === 'string' ? raw.personUid : null,
    personFullName: typeof raw.personFullName === 'string' ? raw.personFullName : null,
    personNationality: typeof raw.personNationality === 'string' ? raw.personNationality : null,
    personDateOfBirth: null,
    personRole: typeof raw.personRole === 'string' ? raw.personRole : null,
    notes: typeof raw.notes === 'string' ? raw.notes : null,
    payloadJson: null,
  };
}

function financialFromHit(raw: Record<string, unknown>, id: number): WatchFinancial {
  return {
    id,
    periodStart: typeof raw.periodStart === 'string' ? raw.periodStart : '2025-01-01',
    periodEnd: typeof raw.periodEnd === 'string' ? raw.periodEnd : '2025-12-31',
    reportKind: typeof raw.reportKind === 'string' ? raw.reportKind : 'annual',
    currency: typeof raw.currency === 'string' ? raw.currency : 'EUR',
    revenue: typeof raw.revenue === 'number' ? raw.revenue : null,
    ebitda: typeof raw.ebitda === 'number' ? raw.ebitda : null,
    liquidity: typeof raw.liquidity === 'number' ? raw.liquidity : null,
    marketValuation: null,
    shareholdingJson: null,
    sourceUrl: null,
    notes: typeof raw.notes === 'string' ? raw.notes : null,
    payloadJson: null,
  };
}

function hasProduct(company: WatchCompany, hit: Record<string, unknown>): boolean {
  const uid = typeof hit.uid === 'string' ? hit.uid : '';
  const name = typeof hit.name === 'string' ? hit.name : '';
  return company.products.some((item) => (
    (uid && item.uid === uid) || (name && item.name === name)
  ));
}

function hasBuilding(company: WatchCompany, hit: Record<string, unknown>): boolean {
  const uid = typeof hit.uid === 'string' ? hit.uid : '';
  const name = typeof hit.name === 'string' ? hit.name : '';
  return company.buildings.some((item) => (
    (uid && item.uid === uid) || (name && item.name === name)
  ));
}

function hasAssociation(company: WatchCompany, hit: Record<string, unknown>): boolean {
  const personUid = typeof hit.personUid === 'string' ? hit.personUid : '';
  const occurredOn = typeof hit.occurredOn === 'string' ? hit.occurredOn : '';
  const eventKind = typeof hit.eventKind === 'string' ? hit.eventKind : '';
  return company.associations.some((item) => (
    (personUid && item.personUid === personUid)
    || (eventKind && occurredOn && item.eventKind === eventKind && item.occurredOn === occurredOn)
  ));
}

function hasFinancial(company: WatchCompany, hit: Record<string, unknown>): boolean {
  const start = typeof hit.periodStart === 'string' ? hit.periodStart : '';
  const end = typeof hit.periodEnd === 'string' ? hit.periodEnd : '';
  return company.financials.some((item) => item.periodStart === start && item.periodEnd === end);
}

function hasKnownLink(state: WatchTowerMockState, hit: Record<string, unknown>): boolean {
  const buyer = typeof hit.buyerWatchCompanyId === 'number' ? hit.buyerWatchCompanyId : 0;
  const supplier = typeof hit.supplierWatchCompanyId === 'number' ? hit.supplierWatchCompanyId : 0;
  if (!buyer || !supplier) {
    return true;
  }
  return state.knownSupplierLinks.some((item) => (
    item.buyerWatchCompanyId === buyer && item.supplierWatchCompanyId === supplier
  ));
}

function applyHits(
  state: WatchTowerMockState,
  companyId: number,
  kind: WatchDossierSearchKind,
  hits: DossierSearchHit[],
): { state: WatchTowerMockState, importedCount: number } {
  const company = state.watchCompanies.find((item) => item.id === companyId);
  if (!company) {
    return { state, importedCount: 0 };
  }
  if (kind === 'known_list') {
    const fresh = hits
      .map((item) => item.payload)
      .filter((payload) => !hasKnownLink(state, payload));
    if (!fresh.length) {
      return { state, importedCount: 0 };
    }
    let nextId = state.knownSupplierLinks.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    const links: WatchKnownSupplierLink[] = fresh.map((payload) => {
      const link: WatchKnownSupplierLink = {
        id: nextId,
        buyerWatchCompanyId: payload.buyerWatchCompanyId as number,
        supplierWatchCompanyId: payload.supplierWatchCompanyId as number,
        watchProductId: typeof payload.watchProductId === 'number' ? payload.watchProductId : null,
        addedBy: typeof payload.addedBy === 'string' ? payload.addedBy : MOCK_OPERATOR,
      };
      nextId += 1;
      return link;
    });
    return {
      importedCount: links.length,
      state: {
        ...state,
        knownSupplierLinks: [...state.knownSupplierLinks, ...links],
      },
    };
  }

  let nextId = nextChildId(state, (item) => {
    if (kind === 'products') {
      return item.products;
    }
    if (kind === 'buildings') {
      return item.buildings;
    }
    if (kind === 'associations') {
      return item.associations;
    }
    return item.financials;
  });
  const addedProducts: WatchProduct[] = [];
  const addedBuildings: WatchBuilding[] = [];
  const addedAssociations: WatchAssociation[] = [];
  const addedFinancials: WatchFinancial[] = [];
  hits.forEach((hit) => {
    if (kind === 'products' && !hasProduct(company, hit.payload)) {
      addedProducts.push(productFromHit(hit.payload, nextId));
      nextId += 1;
    }
    if (kind === 'buildings' && !hasBuilding(company, hit.payload)) {
      addedBuildings.push(buildingFromHit(hit.payload, nextId));
      nextId += 1;
    }
    if (kind === 'associations' && !hasAssociation(company, hit.payload)) {
      addedAssociations.push(associationFromHit(hit.payload, nextId));
      nextId += 1;
    }
    if (kind === 'financials' && !hasFinancial(company, hit.payload)) {
      addedFinancials.push(financialFromHit(hit.payload, nextId));
      nextId += 1;
    }
  });
  const importedCount = addedProducts.length
    + addedBuildings.length
    + addedAssociations.length
    + addedFinancials.length;
  if (!importedCount) {
    return { state, importedCount: 0 };
  }
  return {
    importedCount,
    state: {
      ...state,
      watchCompanies: state.watchCompanies.map((item) => (
        item.id === companyId
          ? {
            ...item,
            products: [...item.products, ...addedProducts],
            buildings: [...item.buildings, ...addedBuildings],
            associations: [...item.associations, ...addedAssociations],
            financials: [...item.financials, ...addedFinancials],
          }
          : item
      )),
    },
  };
}

export function importDossierSearchHits(
  state: WatchTowerMockState,
  input: { chatId: number, companyId: number },
): DossierSearchImportResult {
  const chat = state.createChats.find((item) => item.id === input.chatId);
  if (!chat) {
    return { ok: false, error: 'missing_chat', state };
  }
  if (!state.watchCompanies.some((item) => item.id === input.companyId)) {
    return { ok: false, error: 'missing_company', state };
  }
  const latest = lastDossierSearchExtract(state, input.chatId);
  if (!latest || !latest.extract.positive) {
    return { ok: false, error: 'no_hits', state };
  }
  if (latest.extract.imported) {
    return { ok: false, error: 'already_imported', state };
  }
  const applied = applyHits(state, input.companyId, latest.extract.searchKind, latest.extract.hits);
  if (!applied.importedCount) {
    return { ok: false, error: 'already_imported', state: applied.state };
  }
  const extract: DossierSearchExtract = { ...latest.extract, imported: true };
  return {
    ok: true,
    importedCount: applied.importedCount,
    state: {
      ...withChat(applied.state, input.chatId, { pendingSeeds: [] }),
      createChatMessages: applied.state.createChatMessages.map((item) => (
        item.id === latest.message.id
          ? { ...item, extractedJson: extract as unknown as Record<string, unknown> }
          : item
      )),
    },
  };
}

export function dossierSearchKindOf(chat: WatchAiChat | undefined): WatchDossierSearchKind | null {
  return chat?.dossierSearchKind || null;
}
